/**
 * The right-hand panel's state (next phase, step 3, 2026-10-06): whether it is open, and the question a page asked it
 * to open with ("Ask" beside a card, the chip over a selection, the top bar's button). Pure: the store holds it
 * (lib/copilot/store) and the panel shows the conversation with it (components/copilot/CopilotPanel).
 */
import type { CopilotAbout } from "@/lib/copilot/ask";

/** What a page asks the panel to open with: a question drafted for the composer, and what it is about. */
export interface CopilotAskRequest {
  draft: string;
  about: CopilotAbout | null;
}

/** An ask, numbered: a new number starts the conversation again with its question (the panel keys it by it). */
export interface CopilotAskNumbered extends CopilotAskRequest {
  nonce: number;
}

export interface PanelState {
  isOpen: boolean;
  /** The question the panel was opened with, until it is closed: reopening it drafts nothing again. */
  request: CopilotAskNumbered | null;
  /** The last number given, kept while the panel is closed so no ask is ever taken for an earlier one. */
  nonce: number;
}

export type PanelAction =
  | { type: "open" }
  | { type: "close" }
  | { type: "toggle" }
  | { type: "ask"; request: CopilotAskRequest };

export function panelReducer(state: PanelState, action: PanelAction): PanelState {
  switch (action.type) {
    case "open":
      return state.isOpen ? state : { ...state, isOpen: true };
    case "close":
      return state.isOpen || state.request ? { ...state, isOpen: false, request: null } : state;
    case "toggle":
      return state.isOpen ? { ...state, isOpen: false, request: null } : { ...state, isOpen: true };
    case "ask": {
      const nonce = state.nonce + 1;
      return { isOpen: true, request: { ...action.request, nonce }, nonce };
    }
  }
}

/**
 * Whether a composer holds a message the person has not sent: words of their own (not the question an ask drafted for
 * them, left as it was) or an attached file. A new ask is held while it does.
 */
export function composerHasDraft(text: string, drafted: string, hasFile: boolean): boolean {
  return hasFile || (text.trim() !== "" && text !== drafted);
}

/** What the panel's conversation shows: the ask it started with, and a newer one held while the person has a message
 * unsent. */
export interface PanelAskState {
  shown: CopilotAskNumbered | null;
  pending: CopilotAskNumbered | null;
}

/**
 * The conversation's next state when an ask comes (#1186 review MEDIUM 2). A new ask starts its question where nothing
 * is typed or attached; where the person has a message unsent, it is held for them to confirm, so their words are
 * never thrown away without a word. The ask already shown, or none (the panel closed and opened again), changes
 * nothing. Pure.
 */
export function nextPanelAsk(state: PanelAskState, incoming: CopilotAskNumbered | null, hasDraft: boolean): PanelAskState {
  if (!incoming || incoming.nonce === state.shown?.nonce || incoming.nonce === state.pending?.nonce) return state;
  return hasDraft ? { shown: state.shown, pending: incoming } : { shown: incoming, pending: null };
}

